import { useEffect, useState, useMemo } from 'react'
import { useAuth } from '../context/AuthContext'
import {
  adminListPending,
  adminGetPendingDiff,
  adminDiscardPending,
  adminPublishAll,
  type AdminPendingChange,
  type DiffResult,
  type PublishResult,
} from '../api/client'
import {
  GitBranch,
  GitCommit,
  Trash2,
  ChevronDown,
  ChevronRight,
  ShieldAlert,
  AlertCircle,
  Loader2,
  CheckCircle2,
  XCircle,
} from 'lucide-react'

const ACTION_LABELS: Record<string, string> = {
  edit: 'Edit',
  create: 'Create',
  delete: 'Delete',
  move: 'Move',
}

const ACTION_COLORS: Record<string, string> = {
  edit: 'bg-blue-900/50 text-blue-200 border border-blue-700/40',
  create: 'bg-green-900/50 text-green-200 border border-green-700/40',
  delete: 'bg-red-900/50 text-red-200 border border-red-700/40',
  move: 'bg-amber-900/50 text-amber-200 border border-amber-700/40',
}

export default function AdminPendingChangesPage() {
  const { role } = useAuth()

  const [pending, setPending] = useState<AdminPendingChange[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  // Expanded row id + diff cache
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [diffCache, setDiffCache] = useState<Record<string, DiffResult>>({})
  const [diffLoading, setDiffLoading] = useState<string | null>(null)
  const [diffErr, setDiffErr] = useState<string | null>(null)

  // Discard confirmation
  const [discardTarget, setDiscardTarget] = useState<AdminPendingChange | null>(null)
  const [discarding, setDiscarding] = useState(false)
  const [discardErr, setDiscardErr] = useState<string | null>(null)

  // Publish
  const [publishing, setPublishing] = useState(false)
  const [publishResult, setPublishResult] = useState<PublishResult | null>(null)
  const [publishErr, setPublishErr] = useState<string | null>(null)

  const isAdmin = role === 'admin'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    void (async () => {
      try {
        const data = await adminListPending()
        if (!cancelled) setPending(data)
      } catch {
        if (!cancelled) setErr('Failed to load pending changes.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Load diff when a row is expanded
  useEffect(() => {
    if (!expandedId) return
    if (diffCache[expandedId]) return

    let cancelled = false
    setDiffLoading(expandedId)
    setDiffErr(null)
    void (async () => {
      try {
        const diff = await adminGetPendingDiff(expandedId)
        if (!cancelled) {
          setDiffCache((prev) => ({ ...prev, [expandedId]: diff }))
        }
      } catch {
        if (!cancelled) setDiffErr('Failed to load diff.')
      } finally {
        if (!cancelled) setDiffLoading(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [expandedId, diffCache])

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  const getDiffForExpanded = useMemo(() => {
    if (!expandedId) return null
    return diffCache[expandedId] ?? null
  }, [expandedId, diffCache])

  // ----- Discard -----
  const handleDiscard = async () => {
    if (!discardTarget) return
    setDiscarding(true)
    setDiscardErr(null)
    try {
      await adminDiscardPending(discardTarget.id)
      setPending((prev) => prev.filter((p) => p.id !== discardTarget.id))
      if (expandedId === discardTarget.id) {
        setExpandedId(null)
      }
      setDiscardTarget(null)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to discard change.'
          : 'Failed to discard change.'
      setDiscardErr(msg)
    } finally {
      setDiscarding(false)
    }
  }

  // ----- Publish -----
  const handlePublishAll = async () => {
    setPublishing(true)
    setPublishErr(null)
    setPublishResult(null)
    try {
      const result = await adminPublishAll()
      setPublishResult(result)
      setPending([])
      setDiffCache({})
      setExpandedId(null)
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Publish failed.'
          : 'Publish failed.'
      setPublishErr(msg)
    } finally {
      setPublishing(false)
    }
  }

  // ----- Access denied -----
  if (!isAdmin) {
    return (
      <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8 text-gray-200">
        <div className="flex items-center gap-3 text-amber-400 bg-amber-950/30 border border-amber-900/40 rounded-lg p-6">
          <ShieldAlert className="w-8 h-8 shrink-0" />
          <div>
            <h2 className="text-lg font-semibold">Access denied</h2>
            <p className="text-amber-300/70 text-sm mt-1">You need the admin role to manage pending changes.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[min(100%,72rem)] mx-auto px-4 lg:px-10 py-8 text-gray-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <GitBranch className="w-6 h-6 text-primary-400" />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-100">Pending changes</h1>
              {pending.length > 0 && (
                <span className="text-xs font-medium bg-amber-900/50 text-amber-200 border border-amber-700/40 px-2.5 py-0.5 rounded-full">
                  {pending.length} pending
                </span>
              )}
            </div>
            <p className="text-gray-500 text-sm mt-0.5">Review and publish uncommitted wiki changes</p>
          </div>
        </div>
        <button
          type="button"
          disabled={publishing || pending.length === 0}
          onClick={() => void handlePublishAll()}
          className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-primary-900/40 border border-primary-700/50 text-primary-100 hover:bg-primary-900/60 disabled:opacity-50"
        >
          {publishing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Publishing…
            </>
          ) : (
            <>
              <GitCommit className="w-4 h-4" />
              Publish all
            </>
          )}
        </button>
      </div>

      {/* Publish success */}
      {publishResult && (
        <div className="flex items-center gap-3 text-green-300 bg-green-950/30 border border-green-900/40 rounded-lg p-4 mb-4">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-green-400" />
          <div>
            <p className="font-medium">Published successfully</p>
            <p className="text-sm text-green-300/70 mt-0.5">
              {publishResult.committed} change{publishResult.committed === 1 ? '' : 's'} committed
              {publishResult.commitHash && (
                <> — commit <span className="font-mono text-xs">{publishResult.commitHash}</span></>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Publish error */}
      {publishErr && (
        <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 mb-4">
          <XCircle className="w-5 h-5 shrink-0" />
          <span>{publishErr}</span>
        </div>
      )}

      {/* Error banner */}
      {err && !publishErr && (
        <div className="flex items-center gap-2 text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg p-4 mb-4">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{err}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading pending changes…
        </div>
      )}

      {/* Pending table */}
      {!loading && (
        <div className="rounded-lg border border-gray-800 overflow-hidden">
          {pending.length === 0 && !err && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <GitCommit className="w-10 h-10 mb-2 opacity-40" />
              <p className="text-sm">No pending changes — the wiki is up to date.</p>
            </div>
          )}

          {pending.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-gray-900/80 text-left text-gray-400">
                <tr>
                  <th className="p-3 font-medium w-8"></th>
                  <th className="p-3 font-medium">Path</th>
                  <th className="p-3 font-medium">Title</th>
                  <th className="p-3 font-medium">Action</th>
                  <th className="p-3 font-medium">Author</th>
                  <th className="p-3 font-medium">Created</th>
                  <th className="p-3 font-medium w-[80px]"></th>
                </tr>
              </thead>
              <tbody>
                {pending.map((ch) => {
                  const isExpanded = expandedId === ch.id
                  return (
                    <tr key={ch.id} className="border-t border-gray-800/80">
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => toggleExpand(ch.id)}
                          className="p-1 rounded text-gray-500 hover:text-gray-200 hover:bg-gray-800"
                          title={isExpanded ? 'Collapse diff' : 'Show diff'}
                          aria-label={isExpanded ? 'Collapse diff' : 'Show diff'}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="p-3 font-mono text-xs break-all text-gray-300">
                        {ch.relative_path}
                      </td>
                      <td className="p-3 text-gray-200 max-w-[200px] truncate" title={ch.title}>
                        {ch.title}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full ${
                            ACTION_COLORS[ch.action] ?? 'bg-gray-800 text-gray-400 border border-gray-700/40'
                          }`}
                        >
                          {ACTION_LABELS[ch.action] ?? ch.action}
                        </span>
                      </td>
                      <td className="p-3 text-gray-400">{ch.author}</td>
                      <td className="p-3 text-gray-400 whitespace-nowrap">
                        {new Date(ch.created_at).toLocaleString()}
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => {
                            setDiscardErr(null)
                            setDiscardTarget(ch)
                          }}
                          className="p-2 rounded-lg text-gray-400 hover:bg-red-900/40 hover:text-red-400"
                          title="Discard change"
                          aria-label={`Discard change ${ch.relative_path}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
                {/* Expanded diff row lives as a sibling below */}
                {(() => {
                  if (!expandedId) return null
                  const expandedChange = pending.find((p) => p.id === expandedId)
                  if (!expandedChange) return null
                  const diff = getDiffForExpanded
                  const loading = diffLoading === expandedId
                  return (
                    <tr key={`${expandedId}-diff`}>
                      <td colSpan={7} className="p-0 border-t border-gray-800/60">
                        <div className="bg-gray-950/60 px-4 py-4">
                          {/* Diff loading */}
                          {loading && (
                            <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading diff…
                            </div>
                          )}

                          {/* Diff error */}
                          {diffErr && !loading && (
                            <div className="flex items-center gap-2 text-error-400 text-sm">
                              <AlertCircle className="w-4 h-4" />
                              {diffErr}
                            </div>
                          )}

                          {/* Diff content */}
                          {diff && !loading && (
                            <div className="space-y-4">
                              <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                                Diff preview — <span className="text-gray-400">{expandedChange.relative_path}</span>
                              </p>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Current (left) */}
                                <div>
                                  <div className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-gray-600" />
                                    Current
                                  </div>
                                  <pre className="text-xs leading-relaxed font-mono bg-gray-900/80 border border-gray-800 rounded-lg p-3 max-h-96 overflow-auto text-gray-400 whitespace-pre-wrap break-all">
                                    {diff.currentMarkdown ?? (
                                      <span className="italic text-gray-600">(new file — no current content)</span>
                                    )}
                                  </pre>
                                </div>

                                {/* Pending (right) */}
                                <div>
                                  <div className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-green-500/60" />
                                    Pending
                                  </div>
                                  <pre className="text-xs leading-relaxed font-mono bg-gray-900/80 border border-green-900/40 rounded-lg p-3 max-h-96 overflow-auto text-gray-200 whitespace-pre-wrap break-all">
                                    {diff.pendingMarkdown}
                                  </pre>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })()}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── Discard confirmation modal ── */}
      {discardTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setDiscardTarget(null)}
        >
          <div
            className="w-full max-w-lg bg-gray-950 border border-gray-800 rounded-xl shadow-xl text-gray-200"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <Trash2 className="w-6 h-6 text-red-400" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-100">Discard change</h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Discard the pending change to <span className="font-mono text-xs text-gray-300">{discardTarget.relative_path}</span>?
                    This cannot be undone.
                  </p>
                </div>
              </div>

              {discardErr && (
                <div className="text-sm text-error-400 bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">
                  {discardErr}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setDiscardTarget(null)}
                  className="text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-gray-800/90 border border-gray-700 text-gray-200 hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={discarding}
                  onClick={() => void handleDiscard()}
                  className="inline-flex items-center gap-2 text-sm font-medium rounded-lg px-4 py-2 min-h-[44px] bg-red-900/40 border border-red-700/50 text-red-200 hover:bg-red-900/60 disabled:opacity-50"
                >
                  {discarding ? 'Discarding…' : 'Discard'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
